import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Users, Store, HeartHandshake, TrendingUp, ArrowRight, Megaphone, Camera,
  MapPin, LineChart, Sparkles, Film, CheckCircle2,
} from "lucide-react";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { htmlLangOf } from "../i18n";
import { PublicEventCard } from "../components/PublicEventCard";
import { PartnerForm } from "../components/PartnerForm";
import { Button } from "../components/ui/button";

const HERO_IMG = "https://images.pexels.com/photos/7964479/pexels-photo-7964479.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const COMMUNITY_IMG = "https://images.unsplash.com/photo-1758272133786-ee98adcc6837?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIweW91bmclMjBjb21tdW5pdHklMjB2b2x1bnRlZXJzJTIwZ3JvdXAlMjBjaXR5fGVufDB8fHx8MTc4NzQxNjMyNnww&ixlib=rb-4.1.0&q=85";
const PARTNER_IMG = "https://images.pexels.com/photos/3738387/pexels-photo-3738387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const FIELD_IMG = "https://images.pexels.com/photos/38196135/pexels-photo-38196135.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const PILLARS = [
  { key: "ambassadors", icon: Users },
  { key: "partners", icon: Store },
  { key: "community", icon: HeartHandshake },
  { key: "growth", icon: TrendingUp },
];

const BENEFIT_ICONS = [MapPin, Megaphone, Users, Camera, TrendingUp, HeartHandshake, Sparkles, LineChart];

export default function PublicHome() {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/public/events", { params: { scope: "upcoming" } })
      .then(({ data }) => setEvents(Array.isArray(data) ? data.slice(0, 3) : [])).catch(() => {});
  }, []);

  useSeo({
    title: "VytaliDays — Live Ambassador Activations",
    description: t("pub.heroText"),
    path: "/",
    type: "website",
    lang: htmlLangOf(i18n.language),
  });

  return (
    <div data-testid="public-home">
      {/* HERO */}
      <section className="vy-hero px-4 py-14 sm:px-6 sm:py-20">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="vy-stagger">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-accent" data-testid="hero-eyebrow">
              {t("pub.eyebrow")}
            </p>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-6xl"
              data-testid="hero-title">
              {t("pub.heroTitle")}
            </h1>
            <p className="mt-5 max-w-xl text-base text-white/75 sm:text-lg" data-testid="hero-text">
              {t("pub.heroText")}
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild className="h-14 rounded-full px-7 text-base font-bold">
                <Link to="/events" data-testid="hero-cta-events">
                  {t("pub.ctaEvents")}<ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button asChild variant="outline"
                className="h-14 rounded-full border-white/30 bg-transparent px-7 text-base font-bold text-white hover:bg-white/10 hover:text-white">
                <Link to="/partners" data-testid="hero-cta-partner">{t("pub.ctaPartner")}</Link>
              </Button>
            </div>
            <Link to="/login" data-testid="hero-cta-login"
              className="mt-5 inline-block text-sm font-bold text-white/70 underline-offset-4 hover:text-accent hover:underline">
              {t("pub.nav.login")}
            </Link>
          </div>
          <div className="relative">
            <img src={HERO_IMG} alt={t("pub.heroTitle")} loading="eager"
              className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl" />
            <div className="absolute -bottom-5 left-4 right-4 rounded-2xl bg-card p-4 shadow-xl sm:left-8 sm:right-8">
              <p className="text-sm font-semibold leading-snug">{t("pub.strategic")}</p>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="px-4 py-20 sm:px-6" data-testid="pillars-section">
        <div className="mx-auto max-w-6xl">
          <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{t("pub.whatTitle")}</h2>
          <p className="mt-3 max-w-2xl text-base text-muted-foreground">{t("pub.whatLead")}</p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map(({ key, icon: Icon }) => (
              <div key={key} data-testid={`pillar-${key}`}
                className="rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon className="h-6 w-6" />
                </span>
                <h3 className="mt-4 font-display text-lg font-bold">{t(`pub.pillars.${key}.title`)}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{t(`pub.pillars.${key}.text`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BEFORE / DURING / AFTER */}
      <section className="bg-[hsl(221_83%_13%)] px-4 py-20 text-white sm:px-6" data-testid="phases-section">
        <div className="mx-auto max-w-6xl">
          <h2 className="max-w-3xl font-display text-3xl font-extrabold sm:text-4xl">{t("pub.phasesTitle")}</h2>
          <div className="mt-10 grid gap-5 lg:grid-cols-3">
            {["before", "during", "after"].map((phase, idx) => (
              <div key={phase} data-testid={`phase-${phase}`}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent font-display text-sm font-extrabold text-accent-foreground">
                    {idx + 1}
                  </span>
                  <h3 className="font-display text-xl font-bold">{t(`pub.phases.${phase}.label`)}</h3>
                </div>
                <ul className="mt-4 space-y-2">
                  {t(`pub.phases.${phase}.items`, { returnObjects: true }).map((item) => (
                    <li key={item} className="flex gap-2 text-sm text-white/75">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent" />{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* UPCOMING */}
      <section className="px-4 py-20 sm:px-6" data-testid="upcoming-section">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{t("pub.upcomingTitle")}</h2>
              <p className="mt-2 text-base text-muted-foreground">{t("pub.upcomingLead")}</p>
            </div>
            <Link to="/events" data-testid="upcoming-see-all"
              className="text-sm font-bold text-primary">{t("pub.allEvents")} →</Link>
          </div>
          {events.length === 0 ? (
            <p data-testid="upcoming-empty"
              className="mt-8 rounded-2xl border border-border bg-card p-8 text-center text-sm font-semibold text-muted-foreground">
              {t("pub.noEvents")}
            </p>
          ) : (
            <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {events.map((e) => <PublicEventCard key={e.slug} event={e} />)}
            </div>
          )}
        </div>
      </section>

      {/* PARTNER VALUE */}
      <section className="bg-secondary px-4 py-20 sm:px-6" data-testid="partner-section">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{t("pub.partnerTitle")}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t("pub.partnerText")}</p>
            <p className="mt-5 inline-block rounded-full bg-primary/10 px-4 py-2 text-sm font-bold text-primary">
              {t("pub.retention")}
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {t("pub.partnerBenefits", { returnObjects: true }).map((b, i) => {
                const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
                return (
                  <div key={b} data-testid={`benefit-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
                    <Icon className="h-5 w-5 shrink-0 text-accent" />
                    <span className="text-sm font-semibold">{b}</span>
                  </div>
                );
              })}
            </div>
            <Button asChild className="mt-8 h-14 rounded-full px-7 text-base font-bold">
              <Link to="/partners" data-testid="partner-section-cta">{t("pub.ctaPartnerVytali")}</Link>
            </Button>
          </div>
          <img src={PARTNER_IMG} alt={t("pub.partnerTitle")} loading="lazy"
            className="aspect-[4/3] w-full rounded-3xl object-cover" />
        </div>
      </section>

      {/* AMBASSADORS */}
      <section className="px-4 py-20 sm:px-6" data-testid="ambassador-section">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <img src={FIELD_IMG} alt={t("pub.ambassadorTitle")} loading="lazy"
            className="aspect-[4/3] w-full rounded-3xl object-cover" />
          <div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{t("pub.ambassadorTitle")}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t("pub.ambassadorText")}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {t("pub.ambassadorPoints", { returnObjects: true }).map((p) => (
                <li key={p} className="rounded-full bg-muted px-4 py-2 text-sm font-semibold">{p}</li>
              ))}
            </ul>
            <Button asChild className="mt-8 h-14 rounded-full px-7 text-base font-bold">
              <Link to="/login" data-testid="ambassador-login-cta">{t("pub.ambassadorCta")}</Link>
            </Button>
            <p className="mt-3 text-xs text-muted-foreground">{t("pub.ambassadorNote")}</p>
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="bg-secondary px-4 py-20 sm:px-6" data-testid="community-section">
        <div className="mx-auto grid max-w-6xl items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-extrabold sm:text-4xl">{t("pub.communityTitle")}</h2>
            <p className="mt-4 text-base text-muted-foreground">{t("pub.communityText")}</p>
            <ul className="mt-6 flex flex-wrap gap-2">
              {t("pub.communityTags", { returnObjects: true }).map((tag) => (
                <li key={tag} className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold">
                  {tag}
                </li>
              ))}
            </ul>
            <div className="mt-8 rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-bold">{t("pub.activationTitle")}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{t("pub.activationText")}</p>
            </div>
          </div>
          <img src={COMMUNITY_IMG} alt={t("pub.communityTitle")} loading="lazy"
            className="aspect-[4/3] w-full rounded-3xl object-cover" />
        </div>
      </section>

      {/* VYTALIFLIX */}
      <section className="px-4 py-20 sm:px-6" data-testid="flix-section">
        <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-card p-8 sm:p-12">
          <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Film className="h-6 w-6" />
          </span>
          <h2 className="mt-5 font-display text-3xl font-extrabold sm:text-4xl">{t("pub.flixTitle")}</h2>
          <p className="mt-4 font-display text-lg font-bold text-primary">{t("pub.flixLine")}</p>
          <p className="mt-4 text-base text-muted-foreground">{t("pub.flixText")}</p>
          <p className="mt-6 inline-block rounded-full bg-muted px-4 py-2 text-sm font-bold text-muted-foreground"
            data-testid="flix-cta-disabled">
            {t("pub.flixSoon")}
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="vy-hero px-4 py-20 sm:px-6" data-testid="final-cta-section">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-extrabold text-white sm:text-4xl lg:text-5xl">
            {t("pub.finalTitle")}
          </h2>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Button asChild className="h-14 rounded-full px-7 text-base font-bold">
              <Link to="/events" data-testid="final-cta-events">{t("pub.ctaEvents")}</Link>
            </Button>
            <Button asChild variant="outline"
              className="h-14 rounded-full border-white/30 bg-transparent px-7 text-base font-bold text-white hover:bg-white/10 hover:text-white">
              <Link to="/partners" data-testid="final-cta-partner">{t("pub.ctaPartner")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* PARTNER FORM */}
      <section className="px-4 py-20 sm:px-6" id="partner-form">
        <div className="mx-auto max-w-2xl"><PartnerForm /></div>
      </section>
    </div>
  );
}
